import React from 'react';
import { Smartphone, Users, Info } from 'lucide-react';
import { adminApi } from '../../lib/adminAuth';
import PushComposer from './PushComposer';
import PushHistory from './PushHistory';
import TemplatesPanel from './TemplatesPanel';
import FestivalsPanel from './FestivalsPanel';

const Stat = ({ label, value, testid }) => (
  <div className="soft-card p-4">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-2xl font-bold text-[#1a1a2e]" data-testid={testid}>{value}</p>
  </div>
);

export default function PushNotificationsPage() {
  const [ov, setOv] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [devices, setDevices] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const loadTemplates = React.useCallback(() => adminApi.get('/api/admin/push/templates').then((r) => setTemplates(r.data)).catch(() => {}), []);
  React.useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const load = React.useCallback(() => {
    adminApi.get('/api/admin/push/overview').then((r) => setOv(r.data)).catch(() => {});
    adminApi.get('/api/admin/push/campaigns').then((r) => setItems(r.data)).catch(() => {});
    adminApi.get('/api/admin/push/devices').then((r) => setDevices(r.data)).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="push-notifications-page">
      <div>
        <h1 className="text-lg font-bold text-[#1a1a2e]">Push Notifications</h1>
        <p className="text-xs text-neutral-500">Send alerts to everyone using the Girgit app on Android and iPhone. Bell alerts are copied to phones automatically.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Phones reachable" value={ov?.devices ?? '–'} testid="push-stat-devices" />
        <Stat label="People with app" value={ov?.users ?? '–'} testid="push-stat-users" />
        <Stat label="Android" value={ov?.by_platform?.android ?? '–'} testid="push-stat-android" />
        <Stat label="iPhone" value={ov?.by_platform?.ios ?? '–'} testid="push-stat-ios" />
      </div>
      {ov && ov.devices === 0 && (
        <div className="flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="push-no-devices-note">
          <Info className="w-4 h-4 shrink-0" />
          No phones have registered yet. Phones appear here once people install the app update that asks for notification permission and sign in.
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-5">
        <PushComposer onSent={load} templates={templates} onTemplatesChange={loadTemplates} />
        <PushHistory items={items} onChange={load} />
      </div>
      <FestivalsPanel />
      <TemplatesPanel templates={templates} onChange={loadTemplates} />
      <div className="soft-card p-5" data-testid="push-devices">
        <h2 className="font-semibold text-[#1a1a2e] mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Registered phones</h2>
        {!devices.length && <p className="text-sm text-slate-400">None yet.</p>}
        <div className="divide-y divide-slate-100">
          {devices.map((d) => (
            <div key={d.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`push-device-${d.id}`}>
              <Smartphone className="w-4 h-4 text-slate-400" />
              <span className="flex-1 truncate text-slate-800">{d.name}</span>
              <span className="text-[11px] uppercase text-slate-400">{d.user_type}</span>
              <span className="text-[11px] text-slate-500 w-28 truncate">{d.device_name || d.platform}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
